import React, { useEffect } from "react"
import AfricaChoroplethMap from '../E-Tracker/Charts/Map/AfricaMap';
import PolicyStageChart from '../E-Tracker/Charts/PolicyStageChart';
import DonutChart from '../E-Tracker/Charts/DonutChart/DonutChart';
import SystemsChart from '../E-Tracker/Charts/SystemsChart';
import { getData, getEthiopiaPolicyRegulatoryFramework } from "services/etracker"
import { getHomePageDashboardData } from "../../services/dashboardService"
import ExpandableSystemStats from "./includes/ExpandableSystemStats";
import { useSelector } from "react-redux"
import { Icon } from '@iconify/react';
import StagesExplainer from "components/Common/Explainers/Contents/StagesExplainer";
import CountriesDropdown from "components/Common/countries-dropdown";

const DashNew = () => {
    //TODO: Create single API for fetching dashboard data. Currently being fetched with 3 APIs. Also helpful for loading indicator.
    const [countryID, setCountryID] = React.useState(null);
    const [data, setData] = React.useState();
    const [summaries, setSummaries] = React.useState([]);
    const [systems, setSystems] = React.useState([]);
    const { user } = useSelector((state) => state.Profile)
    const [mapData, setMapData] = React.useState(null);
    const [isMobile, setIsMobile] = React.useState(false);
    const [isTablet, setIsTablet] = React.useState(false);
    const overViewTab = 'number_of_policies';

    // Check if the screen is mobile or tablet size
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
            setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
        }
        
        // Set initial value
        handleResize();
        
        // Add event listener
        window.addEventListener('resize', handleResize);
        
        // Clean up
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const labelMap = {
        number_of_policies: {
            id: 1,
            dataField: 'number_of_policies',
            textField: 'Policies'
        },
        policy_score: {
            id: 2,
            dataField: 'average_score',
            textField: 'Avg. Score'
        },
        legal_framework: {
            id: 2,
            dataField: 'number_of_policies',
            textField: 'Legal framework'
        }
    }

    const [legalFrameworkData, setLegalFrameworkData] = React.useState(null);

    const label = labelMap[overViewTab];

    const fetchDashboardData = async () => {
        try {
            const dashboardData = await getHomePageDashboardData({ countryID });

            if (dashboardData) {
                const fetchedSummaries = dashboardData.summaries?.data || [];
                const fetchedSystems = dashboardData.systems?.data.map(system => ({
                    system: system.system_name,
                    policy_summary: {
                        total: system.policies_summary.total,
                        approved: system.policies_summary.approved,
                        implementation: system.policies_summary.implementation,
                    },
                })) || [];

                setSummaries(fetchedSummaries);
                setSystems(fetchedSystems);
            }
        } catch (e) {
            console.error("Error fetching dashboard data", e);
        }
    };

    const fetchEthiopiaLegalFramework = async () => {
        const response = await getEthiopiaPolicyRegulatoryFramework();
        const _data = response.data;
        // add total property sum of no_of_policies[arr] in data
        _data['name'] = _data.ethiopia_policy_type;
        _data['Legal framework'] = _data.ethiopia_policy_type;
        setLegalFrameworkData(response.data);
    }

    const fetchData = async () => {
        let query = {
            metric: 'number_of_policies',
            ...(countryID && { country_id: countryID })
        };
        setData(await getData(0, query));
        
        // Check if country is Ethiopia (ID 2)
        if (countryID && countryID === 2) {
            fetchEthiopiaLegalFramework();
        } else {
            setLegalFrameworkData(null);
        }
    }

    useEffect(() => {
        if (data && data.location_overview?.data) {
            setMapData(data.location_overview.data)
        }
    }, [data])

    useEffect(() => {
        fetchData();
        fetchDashboardData();
    }, [countryID])

    useEffect(() => {
        if (user?.user_country?.length) {
            setCountryID(user.user_country.length > 1 ? null : user.user_country[0]?.country_id) //can be removed if taking accessible countries in country dropdown
        }
    }, [user])

    const getHugeIconClass = (index) => {
        if (index === 0) {
            return "task-02";
        } else if (index === 1) {
            return "tick-double-03";
        } else if (index === 2) {
            return "legal-hammer";
        }
        return "task-02";
    };
    
    return (
        <div className={`${window.location.pathname == '/dashboard' ? 'page-content' : 'p-2 sm:p-3 bg-gray-50 flex flex-col gap-1'} no-border-chart`}>
            {/* Header with Country Selector and Metrics */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-3 md:p-4 bg-white rounded-lg shadow-sm mb-3">
                {/* Country Selection */}
                <div className="flex items-center country-selection">
                    <span className="mr-2 text-sm md:text-base text-nowrap">Showing data for</span>
                    <CountriesDropdown countryID={countryID} setCountryID={setCountryID} />
                </div>
                
                {/* Key Metrics Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 w-full">
                    {summaries.map((summary, i) => (
                        <div 
                            key={i} 
                            className="flex items-center p-2 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                        >
                            <div className="flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 mr-2 rounded-lg bg-green-50">
                                <Icon 
                                    icon={`hugeicons:${getHugeIconClass(i)}`} 
                                    className="w-3 h-3 sm:w-4 sm:h-4 text-[#036F36]" 
                                />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-sm sm:text-base font-semibold text-gray-800 leading-tight">
                                    {summary.value}
                                </span>
                                <span className="text-xs text-gray-500">
                                    {summary.description}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Main Dashboard Content - Responsive Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* Left Column - Map and Charts */}
                <div className="flex flex-col gap-3">
                    {/* Map */}
                    <div className="bg-white rounded-lg shadow-sm p-3 flex items-center justify-center">
                        <AfricaChoroplethMap 
                            chartData={mapData} 
                            countryID={countryID} 
                            setCountryID={setCountryID} 
                            label={label}
                        />
                    </div>
                    
                    {/* Stages Chart */}
                    <div className="bg-white rounded-lg shadow-sm p-3">
                        <div className="flex flex-col items-center">
                            <div className="w-full flex justify-center">
                                <DonutChart 
                                    chartData={data?.stage_overview?.data} 
                                    title={`Number of policies in different stages`} 
                                    label={label} 
                                />
                            </div>
                            
                            {/* Stages Explainer */}
                            <div className="mt-2 w-full">
                                <StagesExplainer stage="all" previewcontent={(
                                    <div className="flex items-center justify-center border border-dashed border-[#036F36] p-2 rounded-lg w-full">
                                        <div className="flex-shrink-0 mr-2">
                                            <Icon icon="hugeicons:hand-pointing-right-04" className="text-[#036F36] h-4 w-4 sm:h-5 sm:w-5" />
                                        </div>
                                        <p className="text-xs md:text-sm m-0">
                                            Take a minute to understand how APRITS clarifies policy stages. Click here
                                        </p>
                                    </div>
                                )} />
                            </div>
                        </div>
                    </div>
                    
                    {/* Ethiopia-specific Legal Framework - Only visible on larger screens when in 2-column layout */}
                    {countryID && countryID === 2 && legalFrameworkData && (
                        <div className="bg-white rounded-lg shadow-sm p-3">
                            <div className="w-full">
                                <DonutChart 
                                    chartData={legalFrameworkData} 
                                    title={`Policies per legal framework`} 
                                    label={labelMap['legal_framework']}
                                />
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Right Column - Systems Summary */}
                <div className="flex flex-col gap-3">
                    {/* Systems Chart */}
                    <div className="bg-white rounded-lg shadow-sm p-3">
                        <div className="w-full flex justify-center">
                            <SystemsChart 
                                chartData={data?.system_overview?.data} 
                                title={`Number of policies in different systems`} 
                                label={label} 
                            />
                        </div>
                    </div>
                    
                    {/* Systems Summary */}
                    <div className="bg-white rounded-lg shadow-sm p-3">
                        <h3 className="text-base sm:text-xl md:text-2xl my-2 sm:my-3">
                            Summary of policies supported across systems
                        </h3>
                        <div className="overflow-y-auto pr-1 max-h-[300px] sm:max-h-[380px]">
                            <div className="space-y-2">
                                {systems.map((system, i) => (
                                    <ExpandableSystemStats 
                                        key={i} 
                                        title={system.system} 
                                        id={`accordion_${i}`}
                                    >
                                        <div className="my-1 flex items-center gap-2">
                                            <Icon icon="hugeicons:task-02" className="text-[#036F36] h-3 w-3 sm:h-4 sm:w-4" />
                                            <p className="text-[#036F36] text-xs md:text-sm m-0">
                                                {system.policy_summary.total} Policies
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Icon icon="hugeicons:tick-double-03" className="text-[#036F36] h-3 w-3 sm:h-4 sm:w-4" />
                                            <p className="text-xs md:text-sm m-0">
                                                {system.policy_summary.approved} Policies Approved
                                            </p>
                                        </div>
                                        <div className="my-1 flex items-center gap-2">
                                            <Icon icon="hugeicons:legal-hammer" className="text-[#036F36] h-3 w-3 sm:h-4 sm:w-4" />
                                            <p className="text-xs md:text-sm m-0">
                                                {system.policy_summary.implementation} in Implementation
                                            </p>
                                        </div>
                                    </ExpandableSystemStats>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Ethiopia-specific Legal Framework - Only visible on mobile when in 1-column layout */}
                    {countryID && countryID === 2 && legalFrameworkData && isMobile && (
                        <div className="bg-white rounded-lg shadow-sm p-3 lg:hidden">
                            <div className="w-full">
                                <DonutChart 
                                    chartData={legalFrameworkData} 
                                    title={`Policies per legal framework`} 
                                    label={labelMap['legal_framework']}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Full-width Policy Stage Chart */}
            <div className="bg-white rounded-lg shadow-sm p-3 my-3">
                <div className="w-full">
                    <div className="max-w-full overflow-x-auto">
                        <PolicyStageChart 
                            title="Number of policies within systems distribution and stages" 
                            chartData={data?.system_stage_overview?.data} 
                            tab={overViewTab}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const style = document.createElement('style');
style.textContent = `
    /* Global class override */
    .no-border-chart .chart-container {
        border:none
    }
    `;
document.head.appendChild(style);

export default DashNew;